import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ListPlus, Loader2, Sparkles, Trash2, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/PageHeader";
import { Markdown } from "@/components/Markdown";
import { api, errMsg, fdate, fdatetime } from "@/lib/api";
import { useFeature } from "@/context/AuthContext";

const GROUPS = [["buyers", "Tutti gli acquirenti"], ["new", "Nuovi acquirenti"], ["returning", "Acquirenti ricorrenti"], ["lost", "Clienti persi"]];

export const SegmentDialog = ({ open, group: g0, onClose, params, prods }) => {
  const [group, setGroup] = useState(g0 || "buyers");
  const [mode, setMode] = useState("static");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const label = prods.length === 1 ? prods[0].name || prods[0].ean : `${prods.length} prodotti`;
  useEffect(() => { if (open) { setGroup(g0 || "buyers"); setMode("static"); } }, [open, g0]);
  useEffect(() => { setName(`${GROUPS.find((x) => x[0] === group)[1]} · ${label}`); }, [group, label]);
  const dynOk = ["buyers", "lost"].includes(group);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/products/segment", { ...params, name, group, mode: dynOk ? mode : "static", labels: prods.map((p) => p.name || p.ean).slice(0, 5).join(", ") });
      toast.success(<span>Segmento "{data.name}" creato{data.count ? ` con ${data.count} clienti` : ""}. <Link className="underline" to="/comunicazioni">Crea la campagna</Link></span>, { duration: 8000 });
      onClose();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-white" data-testid="pa-segment-dialog">
        <DialogHeader><DialogTitle>Crea segmento dai clienti</DialogTitle></DialogHeader>
        <div className="grid gap-3 text-xs text-slate-500">
          <label>Quali clienti<Select value={group} onValueChange={setGroup}><SelectTrigger className="mt-1" data-testid="pa-segment-group"><SelectValue /></SelectTrigger>
            <SelectContent>{GROUPS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select></label>
          <label>Tipo di segmento<Select value={dynOk ? mode : "static"} onValueChange={setMode} disabled={!dynOk}><SelectTrigger className="mt-1" data-testid="pa-segment-mode"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="static">Lista fissa dei clienti di adesso</SelectItem><SelectItem value="dynamic">Si aggiorna da solo (ultimi {params.days} giorni)</SelectItem></SelectContent></Select></label>
          {!dynOk && <p className="text-[11px] text-slate-400">Nuovi e ricorrenti sono disponibili come lista fissa.</p>}
          <label>Nome<Input className="mt-1" data-testid="pa-segment-name" value={name} onChange={(e) => setName(e.target.value)} /></label>
          <p className="text-[11px] text-slate-400">Periodo {fdate(params.start)} – {fdate(params.end)}. Il segmento compare in Segmenti e in Comunicazioni per inviare SMS, WhatsApp o email.</p>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button data-testid="pa-segment-save" disabled={busy || !name.trim()} className="bg-blue-600 hover:bg-blue-700" onClick={save}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea segmento"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SavedLists = ({ prods, onLoad, canWrite }) => {
  const [lists, setLists] = useState([]);
  const load = () => api.get("/products/lists").then((r) => setLists(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    const name = window.prompt("Nome della lista prodotti", "");
    if (!name?.trim()) return;
    try { await api.post("/products/lists", { name: name.trim(), items: prods }); toast.success("Lista salvata"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (l) => { if (!window.confirm(`Eliminare la lista "${l.name}"?`)) return; try { await api.delete(`/products/lists/${l.id}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="pa-lists">
      <Select value="" onValueChange={(id) => { const l = lists.find((x) => x.id === id); if (l) onLoad(l.items); }}>
        <SelectTrigger className="h-8 w-56 text-xs" data-testid="pa-list-select"><SelectValue placeholder={lists.length ? "Carica una lista salvata" : "Nessuna lista salvata"} /></SelectTrigger>
        <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={l.id}>{l.name} ({l.items.length})</SelectItem>)}</SelectContent>
      </Select>
      {canWrite && <Button size="sm" variant="outline" className="h-8" data-testid="pa-list-save" disabled={!prods.length} onClick={save}><ListPlus className="mr-1 h-4 w-4" />Salva come lista</Button>}
      {canWrite && lists.length > 0 && <Select value="" onValueChange={(id) => del(lists.find((x) => x.id === id))}>
        <SelectTrigger className="h-8 w-9 px-2 text-xs" data-testid="pa-list-delete" title="Elimina lista"><Trash2 className="h-3.5 w-3.5 text-slate-400" /></SelectTrigger>
        <SelectContent>{lists.map((l) => <SelectItem key={l.id} value={l.id}>Elimina "{l.name}"</SelectItem>)}</SelectContent>
      </Select>}
    </div>
  );
};

export const ProductAi = ({ d, params, canWrite }) => {
  const aiOn = useFeature("ai");
  const [doc, setDoc] = useState(d.ai_comment);
  const [busy, setBusy] = useState(false);
  useEffect(() => setDoc(d.ai_comment), [d]);
  const gen = async () => {
    setBusy(true);
    try { setDoc((await api.post("/products/ai-comment", params, { timeout: 90000 })).data); toast.success("Commento IA generato"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!aiOn) return null;
  return (
    <Panel testId="pa-ai-panel" title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-blue-600" />Commento IA</span>}
      action={canWrite && <Button size="sm" data-testid="pa-ai-generate" disabled={busy} className="bg-blue-600 hover:bg-blue-700" onClick={gen}>{busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Analisi in corso...</> : doc ? "Rigenera commento" : "Genera commento IA"}</Button>}>
      <div className="p-5 text-sm">
        {doc ? <><div data-testid="pa-ai-text"><Markdown text={doc.text} /></div><p className="mt-3 border-t border-slate-100 pt-2 text-[11px] text-slate-400">Generato il {fdatetime(doc.created_at)} da {doc.by} per questi prodotti, periodo e sedi</p></>
          : <p className="text-slate-400" data-testid="pa-ai-empty">L'IA spiega l'andamento dei prodotti e l'effetto delle promozioni. Il commento resta salvato per questa selezione.</p>}
      </div>
    </Panel>
  );
};

export const SegmentButton = ({ onClick, testId = "pa-segment-button", label = "Crea segmento" }) => (
  <Button size="sm" variant="outline" data-testid={testId} onClick={onClick}><UsersRound className="mr-1 h-4 w-4" />{label}</Button>
);
